(function () {
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('mnb-bottom-sheet')) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
  var openSheets = [];
  var listeningForKeydown = false;
  var scrollLock = null;

  function lockPageScroll() {
    if (!scrollLock) {
      var body = document.body;
      var docEl = document.documentElement;
      var scrollX = window.scrollX || window.pageXOffset || 0;
      var scrollY = window.scrollY || window.pageYOffset || 0;
      scrollLock = {
        count: 0, scrollX: scrollX, scrollY: scrollY, docOverflow: docEl.style.overflow,
        bodyPosition: body.style.position, bodyTop: body.style.top, bodyLeft: body.style.left,
        bodyRight: body.style.right, bodyWidth: body.style.width
      };
      docEl.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = -scrollY + 'px';
      body.style.left = -scrollX + 'px';
      body.style.right = '0';
      body.style.width = '100%';
    }
    scrollLock.count += 1;
    var released = false;
    return function() {
      if (released) return;
      released = true;
      scrollLock.count -= 1;
      if (scrollLock.count) return;
      var lock = scrollLock;
      scrollLock = null;
      var body = document.body;
      document.documentElement.style.overflow = lock.docOverflow;
      body.style.position = lock.bodyPosition;
      body.style.top = lock.bodyTop;
      body.style.left = lock.bodyLeft;
      body.style.right = lock.bodyRight;
      body.style.width = lock.bodyWidth;
      window.scrollTo(lock.scrollX, lock.scrollY);
    };
  }

  function onDocumentKeydown(event) {
    var top = openSheets[openSheets.length - 1];
    if (top) top._onKeydown(event);
  }

  function syncKeydownListener() {
    var shouldListen = openSheets.length > 0;
    if (shouldListen === listeningForKeydown) return;
    if (shouldListen) document.addEventListener('keydown', onDocumentKeydown);
    else document.removeEventListener('keydown', onDocumentKeydown);
    listeningForKeydown = shouldListen;
  }

  function isBlocked(element) {
    var current = element;
    while (current) {
      if (current.hidden || current.inert === true || current.hasAttribute('inert') || current.getAttribute('aria-hidden') === 'true') return true;
      current = current.parentElement;
    }
    return false;
  }

  function isFocusable(element) {
    if (!element || !element.matches || !element.matches(FOCUSABLE)) return false;
    if (element.matches(':disabled')) return false;
    return element.tabIndex >= 0 && !isBlocked(element);
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isFocusable);
  }

  function snapshot(element) {
    return {
      element: element,
      inert: element.inert === true,
      hadInert: element.hasAttribute('inert'),
      hadAriaHidden: element.hasAttribute('aria-hidden'),
      ariaHidden: element.getAttribute('aria-hidden'),
    };
  }

  function isolate(state) {
    state.element.inert = true;
    state.element.setAttribute('inert', '');
    state.element.setAttribute('aria-hidden', 'true');
  }

  function restore(state) {
    state.element.inert = state.inert;
    if (state.hadInert) state.element.setAttribute('inert', '');
    else state.element.removeAttribute('inert');
    if (state.hadAriaHidden) state.element.setAttribute('aria-hidden', state.ariaHidden);
    else state.element.removeAttribute('aria-hidden');
  }

  class MnbBottomSheet extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._outside = [];
      this._blockedBranch = [];
      this._opener = null;
      this._closeTimer = null;
      this._releasePageScroll = null;
      this._portalHome = null;
      this._isPortaling = false;
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      if (!this._built) {
        var children = Array.prototype.slice.call(this.childNodes);
        var panel = document.createElement('div');
        panel.setAttribute('data-bottom-sheet-panel', '');
        panel.setAttribute('tabindex', '-1');
        children.forEach(function (child) { panel.appendChild(child); });
        var backdrop = document.createElement('div');
        backdrop.setAttribute('data-bottom-sheet-backdrop', '');
        this.appendChild(backdrop);
        this.appendChild(panel);
        this.hidden = true;
        this._built = true;
      }
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (this._isPortaling) return;
      if (!this._connected) return;
      this.close();
      this.removeEventListener('click', this._onClick);
      this._connected = false;
    }

    open() {
      if (this._open) return;
      this._open = true;
      this._releasePageScroll = lockPageScroll();
      if (this._closeTimer) window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
      this._moveToPortal();
      this.classList.remove('is-closing');
      this.removeAttribute('data-bottom-sheet-closing');
      this._opener = document.activeElement;
      this.hidden = false;
      void this.offsetWidth;
      this.classList.add('is-open');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this._blockedBranch = [];
      var path = this;
      while (path) {
        if (path.inert === true || path.hasAttribute('inert') || path.getAttribute('aria-hidden') === 'true') {
          this._blockedBranch.push(snapshot(path));
          path.inert = false;
          path.removeAttribute('inert');
          if (path.getAttribute('aria-hidden') === 'true') path.removeAttribute('aria-hidden');
        }
        path = path.parentElement;
      }
      this._outside = [];
      var branch = this;
      var ancestor = this.parentElement;
      while (ancestor && ancestor !== document.documentElement) {
        Array.prototype.forEach.call(ancestor.children, function (child) {
          if (child === branch) return;
          this._outside.push(snapshot(child));
        }, this);
        branch = ancestor;
        ancestor = ancestor.parentElement;
      }
      this._outside.forEach(isolate);
      openSheets.push(this);
      syncKeydownListener();
      var first = focusables(this)[0];
      (first || this.querySelector('[data-bottom-sheet-panel]')).focus();
      this.dispatchEvent(new CustomEvent('bottom-sheet:open', { bubbles: true }));
    }

    close() {
      if (!this._open) return;
      var index = openSheets.indexOf(this);
      while (index !== -1 && openSheets.length - 1 > index) {
        openSheets[openSheets.length - 1].close();
      }
      if (index !== -1) openSheets.splice(index, 1);
      syncKeydownListener();
      this._open = false;
      if (this._releasePageScroll) this._releasePageScroll();
      this._releasePageScroll = null;
      this.classList.remove('is-open');
      this.classList.add('is-closing');
      this.setAttribute('data-bottom-sheet-closing', '');
      this.hidden = true;
      this.removeAttribute('aria-modal');
      this._outside.forEach(restore);
      this._outside = [];
      this._blockedBranch.forEach(restore);
      this._blockedBranch = [];
      if (isFocusable(this._opener) && this._opener.isConnected) this._opener.focus();
      this.dispatchEvent(new CustomEvent('bottom-sheet:close', { bubbles: true }));
      this._opener = null;
      var self = this;
      this._closeTimer = window.setTimeout(function () {
        if (self._open) return;
        self.classList.remove('is-closing');
        self.removeAttribute('data-bottom-sheet-closing');
        self._restoreFromPortal();
        self._closeTimer = null;
      }, 240);
    }

    _moveToPortal() {
      if (!this.hasAttribute('portal') || !document.body || this.parentElement === document.body) return;
      this._portalHome = { parent: this.parentNode, next: this.nextSibling };
      this._isPortaling = true;
      document.body.appendChild(this);
      this._isPortaling = false;
    }

    _restoreFromPortal() {
      if (!this._portalHome) return;
      var home = this._portalHome;
      this._portalHome = null;
      if (!home.parent) return;
      this._isPortaling = true;
      home.parent.insertBefore(this, home.next && home.next.parentNode === home.parent ? home.next : null);
      this._isPortaling = false;
    }

    _onKeydown(event) {
      if (!this._open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var controls = focusables(this);
      if (!controls.length) {
        event.preventDefault();
        this.querySelector('[data-bottom-sheet-panel]').focus();
        return;
      }
      var index = controls.indexOf(document.activeElement);
      event.preventDefault();
      if (event.shiftKey) controls[(index <= 0 ? controls.length : index) - 1].focus();
      else controls[(index + 1) % controls.length].focus();
    }

    _onClick(event) {
      if (this._open && event.target === this.querySelector('[data-bottom-sheet-backdrop]')) this.close();
    }
  }

  window.customElements.define('mnb-bottom-sheet', MnbBottomSheet);
}());
